/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the GradeListItem component that is displayed at the top of each
 * view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI ListItem component.
 */
import ListItem from '@material-ui/core/ListItem';

/**
 * Material UI ListItemIcon component.
 */
import ListItemIcon from '@material-ui/core/ListItemIcon';

/**
 * Material UI ListItemText component.
 */
import ListItemText from '@material-ui/core/ListItemText';

/**
 * Material UI Fade component.
 */
import Fade from '@material-ui/core/Fade';

/**
 * Material UI Divider component.
 */
import Divider from '@material-ui/core/Divider';

/**
 * Material UI Grid component.
 */
import Grid from '@material-ui/core/Grid';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * SVG icon definition
 */
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';

/**
 * Material UI Link component.
 */
import Link from '@material-ui/core/Link';

/**
 * used to navigate around the app.
 */
import { Link as RouterLink } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of grade data loaded from the ServiceStack API.
 */
import useGradesIndividual from 'src/hooks/useGradesIndividual';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/**
 * changes \n's to <br />'s
 */
import convertReturnsToHtml from 'src/utils/convertReturnsToHtml';

/**
 * Enables the use of global app state.
 */
import { useAppSelector, AppSelectors } from 'src/store/reducers/app';
import SearchHighlighter from './SearchHighlighter';
import * as classNames from 'classnames';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IGradeListItemProps {
    gradeId: number;
    version: Dtos.Version;
    category: Dtos.Category;
    term: Dtos.Term;
    subterm: Dtos.Term | null;
    showDivider?: boolean;
    search?: string;
    getGrade?: (termId: number, gradeId: number) => [Dtos.Grade | null];
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    listItem: {
    },
    listItemDisabled: {
        background: theme.palette.grey[50],
        cursor: 'not-allowed'
    },
    iconGrade1: {
        color: theme.palette.secondary.main,
        opacity: 0.1
    },
    iconGrade2: {
        color: theme.palette.secondary.main,
        opacity: 0.25
    },
    iconGrade3: {
        color: theme.palette.secondary.main,
        opacity: 0.5
    },
    iconGrade4: {
        color: theme.palette.secondary.main,
        opacity: 0.75
    },
    iconGrade5: {
        color: theme.palette.secondary.main,
        opacity: 1
    },
    link: {
        // display: 'block'
    },
    linkDisabled: {
        // display: 'block'
    }
}));

const useGridStyles = makeStyles(theme => ({
    gridRow: {
        width: 'auto',
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid rgba(0, 0, 0, 0.12)`,
        // marginRight: -theme.spacing(2),

        '&:first-child': {
            paddingTop: 0
        },

        '&:last-child': {
            borderBottom: 'none'
        }
    },
    gridTitle: {

    },
    gridValue: {
        alignSelf: 'center'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const GradeListItemGridRow: React.FunctionComponent<{label: string; value: React.ReactNode; }> = ({
    label,
    value
}) => {
    const classes = useGridStyles();

    return (
        <Grid
            container
            className={classes.gridRow}
        >
            <Grid
                item
                className={classes.gridTitle}
                xs={12}
                sm={2}
                md={2}
                lg={2}
                xl={1}
            >
                <Typography
                    variant="h6"
                >
                    {label}
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
                sm={10}
                md={10}
                lg={10}
                xl={11}
                className={classes.gridValue}
            >
                {value}
            </Grid>
        </Grid>
    )
}

const GradeListItem: React.FunctionComponent<IGradeListItemProps> = ({
    gradeId,
    version,
    category,
    term,
    subterm,
    showDivider,
    getGrade,
    search
}) => {
    // Get classnames for styles.
    const classes = useStyles();

    const targetTermId = useAppSelector(AppSelectors.selectTargetTermId);
    const maxGrade = useAppSelector(AppSelectors.selectMaxGrade);
    const minGrade = useAppSelector(AppSelectors.selectMinGrade);

    const [grade] = getGrade ?
        getGrade(subterm ? subterm.id : term.id, gradeId) :
        useGradesIndividual(subterm ? subterm.id : term.id, gradeId);

    const allowed = grade && ((minGrade && parseInt(grade.name, 10) >= minGrade) || !minGrade) && ((maxGrade && parseInt(grade.name, 10) <= maxGrade) || !maxGrade);

    // const gradeLoadState = useGradeSelector(state => GradeSelectors.selectGradeLoadState(state, gradeId));

    return (
        <Fade
            in={true}
        >
            <div>
                <ListItem
                    button={allowed as any}
                    className={classNames(classes.listItem, { [classes.listItemDisabled]: !allowed })}
                    component={allowed ? RouterLink : 'div'}
                    to={`/${version.code}/browse/grade/${gradeId}`}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon
                            fixedWidth
                            icon={faUser}
                            className={classes[`iconGrade${(grade ? grade.name : '1')}`]}
                            size="lg"
                        />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            grade ?
                                search ?
                                    <>
                                        <GradeListItemGridRow
                                            label="Category"
                                            value={
                                                targetTermId ?
                                                    <Typography
                                                        className={classes.linkDisabled}
                                                    >
                                                        <SearchHighlighter
                                                            searchTerms={search}
                                                        >
                                                            {category.name}
                                                        </SearchHighlighter>
                                                    </Typography>:
                                                    <Link
                                                        className={classes.link}
                                                        component={RouterLink}
                                                        to={`/${version.code}/browse/category/${category.id}`}
                                                    >
                                                        <SearchHighlighter
                                                            searchTerms={search}
                                                        >
                                                            {category.name}
                                                        </SearchHighlighter>
                                                    </Link>
                                            }
                                        />
                                        <GradeListItemGridRow
                                            label="Term"
                                            value={
                                                targetTermId && targetTermId !== term.id ?
                                                    <Typography
                                                        className={classes.linkDisabled}
                                                    >
                                                        <SearchHighlighter
                                                            searchTerms={search}
                                                        >
                                                            {term.name}
                                                        </SearchHighlighter>
                                                    </Typography>:
                                                    <Link
                                                        className={classes.link}
                                                        component={RouterLink}
                                                        to={`/${version.code}/browse/term/${term.id}`}
                                                    >
                                                        <SearchHighlighter
                                                            searchTerms={search}
                                                        >
                                                            {term.name}
                                                        </SearchHighlighter>
                                                    </Link>
                                            }
                                        />
                                        {
                                            subterm ?
                                                <GradeListItemGridRow
                                                    label="Subterm"
                                                    value={
                                                        targetTermId && targetTermId !== subterm.id && targetTermId !== term.id ?
                                                            <Typography
                                                                className={classes.linkDisabled}
                                                            >
                                                                <SearchHighlighter
                                                                    searchTerms={search}
                                                                >
                                                                    {subterm.name}
                                                                </SearchHighlighter>
                                                            </Typography> :
                                                            <Link
                                                                className={classes.link}
                                                                component={RouterLink}
                                                                to={`/${version.code}/browse/term/${subterm.id}`}
                                                            >
                                                                <SearchHighlighter
                                                                    searchTerms={search}
                                                                >
                                                                    {subterm.name}
                                                                </SearchHighlighter>
                                                            </Link>
                                                    }
                                                /> :
                                                null
                                        }
                                        <GradeListItemGridRow
                                            label="Grade"
                                            value={
                                                <Typography>
                                                    Grade
                                                    {' '}
                                                    <SearchHighlighter
                                                        searchTerms={search}
                                                    >
                                                        {grade.name}
                                                    </SearchHighlighter>
                                                </Typography>
                                            }
                                        />
                                    </> :
                                    `Grade ${grade.name}` :
                                ''
                        }
                        secondary={grade ?
                            search ?
                                <SearchHighlighter
                                    searchTerms={search}
                                    convertReturns
                                >
                                    {grade.description}
                                </SearchHighlighter> :
                                convertReturnsToHtml(grade.description) :
                                ''
                        }
                    />
                </ListItem>
                {
                    showDivider && (
                        <Divider />
                    )
                }
            </div>
        </Fade>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default GradeListItem;


// WEBPACK FOOTER //
// ./src/components/GradeListItem.tsx